import { useSyncExternalStore } from "react";
import { useTranslation } from "../../lib";
import { DraftSaveStatus } from "../../lib/questionnaire_draft/coordinator";
import { useSelectTheme } from "../../store";

export interface QuestionnaireDraftStatusNoteProps {
  subscribe: (listener: () => void) => () => void;
  getStatus: () => DraftSaveStatus;
  onRetry: () => void;
}

/**
 * Whether the questionnaire editor's recovery copy is being kept. A polite live region, since it says
 * nothing that needs an answer except when saving the copy has stopped.
 */
export const QuestionnaireDraftStatusNote = (props: QuestionnaireDraftStatusNoteProps) => {
  const { subscribe, getStatus, onRetry } = props;
  const status = useSyncExternalStore(subscribe, getStatus);
  const { t } = useTranslation();
  const { color, fontSize, fontWeight, pad } = useSelectTheme();

  let message: string | null = null;
  let warning = false;
  switch (status.state) {
    case "saving":
    case "retrying":
      message = t("questionnaire_draft.status_saving");
      break;
    case "saved":
      // React escapes the text it renders, so i18next must not escape the time as well
      message = t("questionnaire_draft.status_saved", {
        time: new Date(status.updatedAt).toLocaleTimeString(),
        interpolation: { escapeValue: false },
      });
      break;
    case "not_saving":
      message = t(`questionnaire_draft.status_${status.reason}`);
      warning = true;
      break;
    case "removing":
      message = t("questionnaire_draft.status_removing");
      break;
    case "removal_refused":
      message = t("questionnaire_draft.status_removal_refused");
      warning = true;
      break;
    case "idle":
      break;
  }

  return (
    <span
      data-testid="questionnaire_draft_status"
      role="status"
      aria-live="polite"
      css={{
        display: "inline-flex",
        alignItems: "center",
        gap: pad.small,
        marginRight: pad.xl,
        fontSize: fontSize.h7,
        color: warning ? color.textIcon.warning : color.textIcon.secondary,
      }}
    >
      {message}
      {/* it sits inside the task form, where a button without a type would submit the task */}
      {status.state === "not_saving" && status.reason === "refused" && (
        <button
          type="button"
          onClick={onRetry}
          css={{
            border: "none",
            background: "transparent",
            padding: 0,
            color: color.primary,
            fontSize: "inherit",
            fontWeight: fontWeight.bold,
            cursor: "pointer",
          }}
        >
          {t("questionnaire_draft.status_retry_button")}
        </button>
      )}
    </span>
  );
};
